import { DocsService } from '@affine/core/modules/doc';
import { DatabaseMoveOutboxService } from '@affine/core/modules/streamlain/services/database-move-outbox';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  canAttemptBackendRequest,
  isBackendUnavailableError,
} from '@affine/core/utils/backend-availability';
import { I18n } from '@affine/i18n';
import { DatabaseClipboardProvider } from '@blocksuite/affine/shared/adapters';
import { NotificationProvider } from '@blocksuite/affine/shared/services';
import { getSystemWorkspaceStore } from '@blocksuite/affine-block-streamlain';
import type { ExtensionType, Store } from '@blocksuite/store';
import type { FrameworkProvider } from '@toeverything/infra';

import { prepareDatabaseCopy } from './database-clipboard-copy';
import { moveDatabaseDurably } from './database-move-durable';

/** Both editor modes use the same owner. Cloud document sync remains the
 * single owner of move/rebind. A copy never creates an App binding. */
export function createDatabaseClipboardExtension(
  framework: FrameworkProvider
): ExtensionType {
  return {
    setup: di =>
      di.addImpl(DatabaseClipboardProvider, provider => {
        const workspace = framework.get(WorkspaceService).workspace;
        const docs = framework.get(DocsService);
        const notifications = provider.get(NotificationProvider);
        const loadSystemDoc = async (host: Store, docId: string) => {
          const store = getSystemWorkspaceStore(host, docId);
          workspace.engine.doc.addDoc(docId, 'database-subdoc');
          const release = workspace.engine.doc.addPriority(docId, 110);
          try {
            await workspace.engine.doc.waitForDocReady(
              docId,
              AbortSignal.timeout(30_000)
            );
            return store;
          } finally {
            release();
          }
        };
        return {
          copy: async input => {
            const { source, doc, parentId } = input;
            if (
              workspace.id !== source.workspaceId ||
              doc.workspace.id !== workspace.id ||
              !parentId
            ) {
              throw new Error(
                'Database copy destination does not belong to this workspace'
              );
            }
            const opened = docs.open(source.docId);
            try {
              await opened.doc.waitForSyncReady();
              // The copy publishes with the page that hosts it.
              return await prepareDatabaseCopy(input, docId =>
                loadSystemDoc(doc, docId)
              );
            } catch (error) {
              notifications.toast(
                error instanceof Error ? error.message : String(error)
              );
              throw error;
            } finally {
              opened.release();
            }
          },
          move: async ({ source, doc, parentId, index, draft }, steps) => {
            if (
              workspace.id !== source.workspaceId ||
              doc.workspace.id !== workspace.id
            )
              throw new Error('Database move crosses workspaces');
            if (workspace.flavour === 'local') {
              return moveDatabaseDurably({
                insert: steps.insert,
                waitForTargetStored: () =>
                  workspace.engine.doc.waitForUpdated(doc.id),
                detach: steps.detach,
              });
            }
            if (!parentId)
              throw new Error('Database move requires a target parent');
            const move = framework.get(DatabaseMoveOutboxService);
            const payload = {
              operationId: steps.operationId,
              databaseId: source.databaseId,
              source: { docId: source.docId, blockId: source.blockId },
              target: {
                docId: doc.id,
                parentBlockId: parentId,
                index:
                  index ?? doc.getModelById(parentId)?.children.length ?? 0,
                ...('xywh' in draft.props &&
                  typeof draft.props.xywh === 'string' && {
                    xywh: draft.props.xywh,
                  }),
              },
              impactsConfirmed: false,
            };
            if (canAttemptBackendRequest()) {
              try {
                if (!(await move.confirm(payload, steps.signal))) {
                  steps.cancel();
                  return 'deferred';
                }
                payload.impactsConfirmed = true;
              } catch (error) {
                if (!isBackendUnavailableError(error)) throw error;
              }
            }
            if (steps.signal.aborted) return 'deferred';
            await move.request(payload, steps.signal);
            notifications.toast(I18n['com.streamlain.databaseMove.pending']());
            return 'deferred';
          },
        };
      }),
  };
}
